import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type Row,
  Table,
  type TableColumns,
  useDefaultSorting,
} from '@bp/ui-components';
import { type DocumentType, type FragmentType, getFragmentData, graphql } from 'client/gql';
import { BpLink } from 'components/BpLink/BpLink';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { BpPermissionChecker } from 'utils/PermissionChecker';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import type { ColumnType } from '../../utils/type-guards';

const CourseWorkMaterialsTableFieldsDocument = graphql(`
  fragment CourseWorkMaterialsTableFields on WorkMaterial {
    uuid
    __typename
    title
    holder {
      __typename
      ... on TeachingUnit {
        uuid
        __typename
        title
        organization {
          uuid
          __typename
        }
      }
    }
    owner {
      uuid
      __typename
    }
    material {
      uuid
      __typename
      fileEntries {
        uuid
        __typename
      }
    }
    visibleAfterVisibleTo
  }
`);

type WorkMaterial = DocumentType<typeof CourseWorkMaterialsTableFieldsDocument>;

type CourseWorkMaterialsTableProps = {
  data: Array<FragmentType<typeof CourseWorkMaterialsTableFieldsDocument>>;
  isGroupEditor: boolean;
  onClick?: (uuid: string) => void;
  onEdit?: (uuid: string) => void;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  onDelete: (uuid: string, title: string) => void;
  permission: BpPermissionChecker | null;
};

export const CourseWorkMaterialsTable: FC<CourseWorkMaterialsTableProps> = ({
  data,
  onDelete,
  isGroupEditor,
  onClick,
  onEdit,
  emptyStateTitle,
  emptyStateSubtitle,
  permission,
}) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const { courseUuid } = useParams<{
    courseUuid: string;
  }>();

  const { sorting, onSortingChange } = useDefaultSorting([{ id: 'title', desc: false }]);

  const memoizedData = useMemo(() => {
    return getFragmentData(CourseWorkMaterialsTableFieldsDocument, data);
  }, [data]);
  const material = memoizedData[0];

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const memoizedColumns = useMemo((): TableColumns<WorkMaterial>[] => {
    return [
      {
        id: 'title',
        size: 400,
        canExpand: true,
        header: t('common.name'),
        accessorKey: 'title',
        meta: {
          filterName: 'Name',
        },
        cell: ({ row }: { row: Row<WorkMaterial> }) => {
          return (
            <BpLink
              value={row.original.title}
              onNavigate={onClick ? () => onClick(row.original.uuid) : undefined}
              isEditorOfCourse={isGroupEditor}
            />
          );
        },
      },
      ...(isGroupEditor
        ? [
            {
              id: 'teachingunit',
              size: 250,
              header: t('teachingUnits.titleSingular'),
              accessorKey: 'holder.title',
              meta: {
                filterName: t('teachingUnits.titleSingular') as string,
              },
            },
          ]
        : []),
      {
        id: 'files',
        accessorKey: 'fileEntries',
        size: 120,
        cell: (cell) => {
          return <>{cell.row.original.material.fileEntries.length}</>;
        },
        header: t('common.documents'),
        meta: {
          filterName: t('common.documents') as string,
        },
      },

      {
        id: 'timeFrame',
        accessorKey: 'timeFrame',
        header: t('common.relevantTimeFrame'),
        size: 200,
        meta: {
          filterName: t('common.relevantTimeFrame') as string,
        },
        enableHiding: true,
      },

      ...(isGroupEditor
        ? [
            {
              id: 'visibleAfterVisibleTo',
              accessorKey: 'visibleAfterVisibleTo',
              size: 150,
              header: t('common.visibleAfter'),
              type: 'boolean' as ColumnType<WorkMaterial>,
              meta: {
                filterName: t('common.visibleAfterVisibleTo') as string,
              },
            },
          ]
        : []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupEditor, onClick]);

  return (
    <>
      <Table<WorkMaterial>
        showSort
        sorting={sorting}
        hideHeader={memoizedData.length === 0}
        onSortingChange={onSortingChange}
        columns={memoizedColumns}
        data={memoizedData}
        isDesktop={isDesktop}
        customPadding='var(--spacing-6)'
        emptyStateSettings={{
          title: emptyStateTitle ?? t('workmaterials.noWorkmaterials'),
          subtitle: emptyStateSubtitle ?? t('workmaterials.createHintCourse'),
          hideIcon: true,
          padding: 'xl',
        }}
        customLastColSpacing='var(--spacing-4)'
        lastCol={(row) => {
          return permission?.canUpdateWorkmaterial(
            {
              uuid: courseUuid ?? '',
              organization: { uuid: material.holder.organization?.uuid },
            },
            {
              owner: {
                uuid: material.owner?.uuid,
                organization: { uuid: material.holder.organization?.uuid },
              },
              workmaterialUuid: material.uuid,
            },
          ) ? (
            <Dropdown noPadding trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
              <DropdownMenu
                data-side='left'
                data={[
                  {
                    onClick: () => {
                      if (onEdit) {
                        onEdit(row.original.uuid);
                      }
                    },
                    label: t('common.edit'),
                  },

                  {
                    type: 'ruler',
                  },

                  {
                    color: 'error',
                    label: t('delete.delete'),
                    onClick: async () => {
                      onDelete(row.original.uuid, row.original.title);
                    },
                  },
                ]}
              />
            </Dropdown>
          ) : undefined;
        }}
      />
    </>
  );
};
